<template>
  <div class="menu-popup-host" :class="{ 'menu-popup-host--fixed': fixed }">
    <Popup
      ref="menuPopup"
      :active="isPopupOpen"
      animation="fade"
      :transparentBg="false"
      :fullsize="false"
      :align="isDropdown ? 'left' : 'right'"
      :valign="'bottom'"
      :hasBg="isMobile"
      :borderRadiusBySide="borderRadiusBySide"
      showHeader
      :headerVariant="isMobile && showMenuTabBar ? 'tabs' : 'default'"
      v-bind="{
        modalContainerStyles: menuContainerStyle,
      }"
      @close="toggleClose"
    >
      <template v-slot:modalHeader>
        <template v-if="isMobile && showMenuTabBar">
          <MenuTabBar
            :active-page="activePopupPage"
            :schedule-count="menuScheduleCount"
            @select="onMenuTabSelect"
          />
          <div v-if="activePopupPage === PopupPage.betDetail" class="bet-detail-subheader">
            <Nav :imageURL="activePopupPage" :toURL="toURL" :popupLevel="popupLevel(activePopupPage)">
              {{ $t(activePopupPage) }}
            </Nav>
          </div>
        </template>
        <div v-else class="modal-header-wrapper">
          <Nav :imageURL="activePopupPage" :toURL="toURL" :popupLevel="popupLevel(activePopupPage)">
            <template
              v-if="!isMobile && withCommission && [PopupPage.history, PopupPage.statement].includes(activePopupPage)"
            >
              <ToggleBtn
                :leftTxt="$t('History')"
                :rightTxt="$t('Statement')"
                :value="activePopupPage === PopupPage.statement"
                @toggle="toggleReport"
              />
            </template>
            <template
              v-else-if="
                !isMobile && [PopupPage.help, PopupPage.jackpotHelp].includes(activePopupPage) && config.enableJackpot
              "
            >
              <ToggleBtn
                :leftTxt="$t('General')"
                :rightTxt="$t('Jackpot')"
                :value="activePopupPage === PopupPage.jackpotHelp"
                @toggle="toggleHelp"
              />
            </template>
            <template v-else>
              {{ $t(activePopupPage) }}
            </template>
          </Nav>
          <div v-if="!isMobile" class="close-btn" @click="toggleClose">
            <img src="@/assets/images/close.webp" />
          </div>
        </div>
      </template>
      <div class="menu-popup-shell">
        <div class="menu-popup-body">
          <MenuMain v-if="!isMobile && activePopupPage === PopupPage.main" />
          <BetHistory v-if="activePopupPage === PopupPage.history" />
          <Statement v-if="activePopupPage === PopupPage.statement" />
          <BetHistoryDetail v-if="activePopupPage === PopupPage.betDetail" />
          <NotificationPopup v-if="activePopupPage === PopupPage.notification" />
          <SchedulePopup v-if="activePopupPage === PopupPage.schedule" />
          <ScheduleDetailPopup v-if="activePopupPage === PopupPage.scheduleDetail" />
          <HelpContent v-if="activePopupPage === PopupPage.help" :isHelpGuide="true" />
          <HelpContent v-if="activePopupPage === PopupPage.jackpotHelp" :helpSource="luckyDrawInstantPrizeHelpGuide" />
          <TNC v-if="activePopupPage === PopupPage.tnc" />
          <JackpotPhysicalPopup v-if="activePopupPage === PopupPage.jackpotPhysicalHelp" />
        </div>
        <MenuFooter v-if="isMobile" @close="toggleClose" />
      </div>
    </Popup>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { isMobile } from 'mobile-device-detect';
import Popup from '@/components/popup/index.vue';
import BetHistory from '@/components/menus/bet-history.vue';
import Statement from '@/components/menus/statement.vue';
import BetHistoryDetail from '@/components/menus/bet-history-detail.vue';
import NotificationPopup from '@/components/popup/notification.vue';
import SchedulePopup from '@/components/popup/schedule.vue';
import ScheduleDetailPopup from '@/components/popup/schedule-detail.vue';
import JackpotPhysicalPopup from '@/components/popup/jackpot-physical-prize';
import Nav from '@/components/menus/nav.vue';
import MenuMain from '@/components/menus/index.vue';
import MenuTabBar from '@/components/menus/menu-tab-bar.vue';
import MenuFooter from '@/components/menus/menu-footer.vue';
import HelpContent from '@/components/help/index.vue';
import TNC from '@/components/menus/tnc/index.vue';
import ToggleBtn from '@/components/toggle-btn.vue';
import { PopupPage, Orientation, EventType } from '@/utils/index.model';
import { getLuckyDrawInstantPrizeHelpGuide } from '@/utils/game-help.ts';

// Space the desktop dropdown keeps clear of the column's right and bottom edges.
const DROPDOWN_GAP = 16;

export default {
  components: {
    Popup,
    BetHistory,
    Statement,
    BetHistoryDetail,
    NotificationPopup,
    SchedulePopup,
    ScheduleDetailPopup,
    JackpotPhysicalPopup,
    Nav,
    MenuMain,
    MenuTabBar,
    MenuFooter,
    HelpContent,
    TNC,
    ToggleBtn,
  },
  props: {
    fixed: {
      type: Boolean,
      default: false,
    },
    /*
     * Pixels of chrome above the popup, for a host that has a header but no
     * bottom bar -- the desktop v2 client. The default keeps the v1 desktop
     * geometry (`calc(100% - 180px)`, vertically centred) exactly as it was.
     */
    /*
     * Where the panel's left edge sits, measured from the left of the host
     * column: given, the panel drops down there under its header control
     * instead of docking to the right edge. Null keeps the 45% right-aligned
     * panel every other caller gets.
     */
    leftInset: {
      type: Number,
      default: null,
    },
    topInset: {
      type: Number,
      // null, not 0: zero is a real inset (the header fully scrolled away) and
      // has to keep the pinned geometry rather than fall back to v1's.
      default: null,
    },
  },
  created() {
    this.updateSchedules();
  },
  data() {
    return {
      PopupPage,
      isMobile,
    };
  },
  computed: {
    ...mapGetters({
      isPopupOpen: 'menu/isPopupOpen',
      isJackpotPopupOpen: 'menu/isJackpotPopupOpen',
      activePopupPage: 'menu/activePopupPage',
      withCommission: 'general/withCommission',
      config: 'general/config',
      schedules: 'schedule/schedules',
    }),
    luckyDrawInstantPrizeHelpGuide() {
      return getLuckyDrawInstantPrizeHelpGuide(this.config.taxPercent, this.config.language);
    },
    showMenuTabBar() {
      return [
        PopupPage.history,
        PopupPage.statement,
        PopupPage.betDetail,
        PopupPage.help,
        PopupPage.jackpotHelp,
        PopupPage.notification,
        PopupPage.schedule,
        PopupPage.scheduleDetail,
        PopupPage.tnc,
        PopupPage.jackpotPhysicalHelp,
      ].includes(this.activePopupPage);
    },
    menuScheduleCount() {
      return this.schedules.filter((schedule) => schedule.eventType === EventType.current).length;
    },
    toURL() {
      switch (this.activePopupPage) {
        case PopupPage.limits:
        case PopupPage.history:
        case PopupPage.statement:
        case PopupPage.help:
        case PopupPage.settings:
        case PopupPage.notification:
        case PopupPage.schedule:
          return isMobile ? '' : PopupPage.main;
        case PopupPage.betDetail:
          return PopupPage.history;
        case PopupPage.tnc:
          return PopupPage.help;
        case PopupPage.main:
        case PopupPage.jackpot:
        case PopupPage.jackpotPhysicalHelp:
          return '';
        case PopupPage.jackpotHistory:
        case PopupPage.jackpotLuckyDraw:
          return PopupPage.jackpot;
        case PopupPage.scheduleDetail:
          return PopupPage.schedule;
        case PopupPage.jackpotHelp:
          return isMobile ? '' : this.isPopupOpen ? '' : PopupPage.jackpot;
        default:
          return '';
      }
    },
    isMobileLandscape() {
      return isMobile && this.$screen.orientation === Orientation.landscape;
    },
    isMobileVertical() {
      return isMobile && this.$screen.orientation === Orientation.portrait;
    },
    // Desktop v2: a dropdown under Game Help rather than a right-edge drawer.
    isDropdown() {
      return !isMobile && this.topInset !== null && this.leftInset !== null;
    },
    borderRadiusBySide() {
      if (isMobile) return ['16px', '16px', '0', '0'];
      return this.isDropdown ? ['16px', '16px', '16px', '16px'] : ['16px', '0', '0', '16px'];
    },
    menuContainerStyle() {
      if (this.isMobileVertical) {
        return {
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          height: 'calc(100% - 320px)',
          minHeight: '524px',
          maxHeight: 'calc(100dvh - 80px)',
        };
      }
      if (this.isMobileLandscape) {
        return {
          position: 'relative',
          minWidth: '45%',
          maxWidth: '45%',
          height: '100% ',
        };
      }
      if (this.topInset !== null) {
        // Pinned under the header rather than centred: the 180px of the v1
        // geometry assumes a bottom bar that the v2 client does not have, so
        // centring left an equal strip of dead space below the popup.
        if (this.isDropdown) {
          // Dropped down under the control that owns it: its left edge is that
          // control's, with its own width and a gap below, clear of the edges.
          return {
            position: 'relative',
            width: '45%',
            minWidth: 0,
            maxWidth: `calc(100% - ${this.leftInset + DROPDOWN_GAP}px)`,
            marginLeft: `${this.leftInset}px`,
            marginTop: `${this.topInset}px`,
            marginBottom: `${DROPDOWN_GAP}px`,
            height: `calc(100% - ${this.topInset + DROPDOWN_GAP}px)`,
          };
        }
        return {
          position: 'relative',
          minWidth: '45%',
          maxWidth: '45%',
          marginTop: `${this.topInset}px`,
          marginBottom: 0,
          height: `calc(100% - ${this.topInset}px)`,
        };
      }
      return {
        position: 'relative',
        minWidth: '45%',
        maxWidth: '45%',
        height: 'calc(100% - 180px)',
      };
    },
  },
  methods: {
    ...mapActions('menu', ['updateMenu']),
    ...mapActions('general', ['updateGeneral']),
    ...mapActions('schedule', ['updateSchedules']),
    popupLevel(page) {
      if (isMobile && page !== PopupPage.jackpot && page !== PopupPage.jackpotPhysicalHelp) return 2;
      if (
        [
          PopupPage.tnc,
          PopupPage.betDetail,
          PopupPage.jackpotHistory,
          PopupPage.jackpotLuckyDraw,
          PopupPage.scheduleDetail,
        ].includes(page)
      ) {
        return 2;
      }
      if (this.isJackpotPopupOpen && page === PopupPage.jackpotHelp) return 2;

      return 1;
    },
    toggleReport(val) {
      this.updateMenu({
        activePopupPage: !val ? PopupPage.statement : PopupPage.history,
      });
      if (!val) {
        this.updateGeneral({
          constrols: {
            historySelection: '',
          },
        });
      }
    },
    toggleHelp(val) {
      this.updateMenu({
        activePopupPage: val ? PopupPage.help : PopupPage.jackpotHelp,
      });
    },
    toggleClose() {
      this.updateMenu({
        isPopupOpen: false,
        activePopupPage: isMobile ? PopupPage.history : PopupPage.main,
      });
      this.updateGeneral({
        constrols: {
          historySelection: '',
        },
      });
    },
    onMenuTabSelect(page) {
      this.updateMenu({ activePopupPage: page });
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-popup-host--fixed {
  ::v-deep .global-modal {
    position: fixed;
    inset: 0;
    z-index: 4999;
  }
}

.menu-popup-shell {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
}

.menu-popup-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ::v-deep > * {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

.bet-detail-subheader {
  height: 56px;
  padding: 12px 0px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;

  ::v-deep .page-nav {
    width: 100%;
  }
}

.modal-header-wrapper {
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  justify-content: flex-start;

  .close-btn {
    width: 32px;
    height: 32px;
    position: absolute;
    right: 0;
    top: 0;
    display: block;
    background-image: linear-gradient(358deg, #bcbfc3 99%, #f0f0f0 1%);
    border-radius: 52%;
    cursor: pointer;

    img {
      width: 14px;
      height: 14px;
      margin: 9px;
    }
  }
}
</style>
